import { useState, useEffect, useCallback, useMemo, lazy, Suspense } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { favoritesAPI, eventsAPI, geocodeAPI, applicationsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { EventCard } from '../components/EventCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { EVENT_TYPES, formatDate, formatEventDates, formatCurrency, getDaysUntil, isEventExpired } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '../lib/utils';
import { 
  CalendarDays, 
  LayoutGrid, 
  TableIcon, 
  MapPin, 
  Loader2, 
  Heart,
  Clock,
  DollarSign,
  ExternalLink,
  Settings,
  AlertCircle,
  Home,
  Navigation,
  FileText,
  CheckCircle2,
  Filter,
  X,
  Users,
  Search,
  CalendarIcon,
  Bookmark,
  BookmarkPlus,
  Square,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Trash2,
  EyeOff,
  CalendarRange,
  Star
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { HelpButton } from '../components/HelpButton';
import { YearView } from '../components/events/YearView';
import { MapView } from '../components/events/MapView';
import { CardsView } from '../components/events/CardsView';
import { TableView } from '../components/events/TableView';
import { CalendarView } from '../components/events/CalendarView';
import {
  PRIORITY_COLORS,
  PILL_COLORS,
  getEventPriority,
  sortEventsByPriority,
} from '../components/events/eventPriority';

// State coordinates for map positioning
const STATE_COORDS = {
  'Alabama': { lat: 32.806671, lng: -86.791130 },
  'Alaska': { lat: 61.370716, lng: -152.404419 },
  'Arizona': { lat: 33.729759, lng: -111.431221 },
  'Arkansas': { lat: 34.969704, lng: -92.373123 },
  'California': { lat: 36.116203, lng: -119.681564 },
  'Colorado': { lat: 39.059811, lng: -105.311104 },
  'Connecticut': { lat: 41.597782, lng: -72.755371 },
  'Delaware': { lat: 39.318523, lng: -75.507141 },
  'Florida': { lat: 27.766279, lng: -81.686783 },
  'Georgia': { lat: 33.040619, lng: -83.643074 },
  'Hawaii': { lat: 21.094318, lng: -157.498337 },
  'Idaho': { lat: 44.240459, lng: -114.478828 },
  'Illinois': { lat: 40.349457, lng: -88.986137 },
  'Indiana': { lat: 39.849426, lng: -86.258278 },
  'Iowa': { lat: 42.011539, lng: -93.210526 },
  'Kansas': { lat: 38.526600, lng: -96.726486 },
  'Kentucky': { lat: 37.668140, lng: -84.670067 },
  'Louisiana': { lat: 31.169546, lng: -91.867805 },
  'Maine': { lat: 44.693947, lng: -69.381927 },
  'Maryland': { lat: 39.063946, lng: -76.802101 },
  'Massachusetts': { lat: 42.230171, lng: -71.530106 },
  'Michigan': { lat: 43.326618, lng: -84.536095 },
  'Minnesota': { lat: 45.694454, lng: -93.900192 },
  'Mississippi': { lat: 32.741646, lng: -89.678696 },
  'Missouri': { lat: 38.456085, lng: -92.288368 },
  'Montana': { lat: 46.921925, lng: -110.454353 },
  'Nebraska': { lat: 41.125370, lng: -98.268082 },
  'Nevada': { lat: 38.313515, lng: -117.055374 },
  'New Hampshire': { lat: 43.452492, lng: -71.563896 },
  'New Jersey': { lat: 40.298904, lng: -74.521011 },
  'New Mexico': { lat: 34.840515, lng: -106.248482 },
  'New York': { lat: 42.165726, lng: -74.948051 },
  'North Carolina': { lat: 35.630066, lng: -79.806419 },
  'North Dakota': { lat: 47.528912, lng: -99.784012 },
  'Ohio': { lat: 40.388783, lng: -82.764915 },
  'Oklahoma': { lat: 35.565342, lng: -96.928917 },
  'Oregon': { lat: 44.572021, lng: -122.070938 },
  'Pennsylvania': { lat: 40.590752, lng: -77.209755 },
  'Rhode Island': { lat: 41.680893, lng: -71.511780 },
  'South Carolina': { lat: 33.856892, lng: -80.945007 },
  'South Dakota': { lat: 44.299782, lng: -99.438828 },
  'Tennessee': { lat: 35.747845, lng: -86.692345 },
  'Texas': { lat: 31.054487, lng: -97.563461 },
  'Utah': { lat: 40.150032, lng: -111.862434 },
  'Vermont': { lat: 44.045876, lng: -72.710686 },
  'Virginia': { lat: 37.769337, lng: -78.169968 },
  'Washington': { lat: 47.400902, lng: -121.490494 },
  'West Virginia': { lat: 38.491226, lng: -80.954453 },
  'Wisconsin': { lat: 44.268543, lng: -89.616508 },
  'Wyoming': { lat: 42.755966, lng: -107.302490 },
  'District of Columbia': { lat: 38.9072, lng: -77.0369 }
};

// State abbreviation to full name mapping
const STATE_ABBREVIATIONS = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas',
  'CA': 'California', 'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware',
  'FL': 'Florida', 'GA': 'Georgia', 'HI': 'Hawaii', 'ID': 'Idaho',
  'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa', 'KS': 'Kansas',
  'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi',
  'MO': 'Missouri', 'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada',
  'NH': 'New Hampshire', 'NJ': 'New Jersey', 'NM': 'New Mexico', 'NY': 'New York',
  'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio', 'OK': 'Oklahoma',
  'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah',
  'VT': 'Vermont', 'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia',
  'WI': 'Wisconsin', 'WY': 'Wyoming', 'DC': 'District of Columbia'
};


// Normalize state name (convert abbreviation to full name)
const normalizeStateName = (state) => {
  if (!state) return null;
  const trimmed = state.trim();
  // If it's an abbreviation (2 characters), convert to full name
  if (trimmed.length === 2) {
    return STATE_ABBREVIATIONS[trimmed.toUpperCase()] || trimmed;
  }
  // Already a full name, return as-is (with proper casing)
  return trimmed;
};

// Haversine formula for distance calculation
const haversineDistance = (lat1, lng1, lat2, lng2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return Math.round(R * c);
};

// Get coordinates for a state (fallback)
const getStateCoords = (state) => {
  if (!state) return null;
  const normalized = normalizeStateName(state);
  return STATE_COORDS[normalized] || null;
};

export const Events = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [myEvents, setMyEvents] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [communityEvents, setCommunityEvents] = useState([]);
  const [userCreatedEvents, setUserCreatedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coordsCache, setCoordsCache] = useState({});
  const [homeCoords, setHomeCoords] = useState(null);
  const [applicationStatusMap, setApplicationStatusMap] = useState({});  // event_id -> { id, status }
  const [favoriteIds, setFavoriteIds] = useState(new Set());  // Set of favorited event IDs
  const [lastRefresh, setLastRefresh] = useState(Date.now());  // Track last refresh time
  const [expiredCommunityCount, setExpiredCommunityCount] = useState(0);  // Count of expired community events
  const [deletingExpired, setDeletingExpired] = useState(false);  // Loading state for delete operation
  
  // Tab for Saved vs All/Community events — defaults to 'all' (all my events)
  const [eventTab, setEventTab] = useState(() => {
    return localStorage.getItem('eventsTab') || 'all';
  });
  
  // Event Filters (available on all tabs)
  const [showFilters, setShowFilters] = useState(false);
  const [filterEventType, setFilterEventType] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState(null);
  const [filterDateTo, setFilterDateTo] = useState(null);
  const [filterMaxDistance, setFilterMaxDistance] = useState('');
  const [filterMinCost, setFilterMinCost] = useState('');
  const [filterMaxCost, setFilterMaxCost] = useState('');
  const [filterVenueType, setFilterVenueType] = useState('');
  const [filterKeywords, setFilterKeywords] = useState('');
  
  // Saved Filters
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveFilterDialogOpen, setSaveFilterDialogOpen] = useState(false);
  const [filterName, setFilterName] = useState('');
  
  // Load viewMode from localStorage, default to 'calendar'
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('eventsViewMode') || 'calendar';
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Sorting state - default to 'date' (earliest first)
  const [sortBy, setSortBy] = useState(() => {
    return localStorage.getItem('eventsSortBy') || 'date';
  });

  // Hide Expired Events toggle - defaults to ON (true)
  const [hideExpired, setHideExpired] = useState(() => {
    const stored = localStorage.getItem('eventsHideExpired');
    return stored === null ? true : stored === 'true';
  });

  // Save eventTab to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('eventsTab', eventTab);
  }, [eventTab]);

  // Save viewMode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('eventsViewMode', viewMode);
  }, [viewMode]);

  // Save sortBy to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('eventsSortBy', sortBy);
  }, [sortBy]);

  // Save hideExpired to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('eventsHideExpired', hideExpired.toString());
  }, [hideExpired]);

  // Load saved filters from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('eventSavedFilters');
    if (saved) {
      try {
        setSavedFilters(JSON.parse(saved));
      } catch (e) {
        console.warn('Failed to load saved filters');
      }
    }
  }, []);

  // Check if any filter is active
  const hasActiveFilters = filterEventType || filterDateFrom || filterDateTo || 
    filterMaxDistance || filterMinCost || filterMaxCost || filterVenueType || filterKeywords;

  // Clear all filters
  const clearFilters = () => {
    setFilterEventType('');
    setFilterDateFrom(null);
    setFilterDateTo(null);
    setFilterMaxDistance('');
    setFilterMinCost('');
    setFilterMaxCost('');
    setFilterVenueType('');
    setFilterKeywords('');
  };

  // Save current filter configuration
  const saveCurrentFilter = () => {
    if (!filterName.trim()) {
      toast.error('Please enter a name for this filter');
      return;
    }
    
    const newFilter = {
      id: Date.now().toString(),
      name: filterName.trim(),
      eventType: filterEventType,
      dateFrom: filterDateFrom?.toISOString() || null,
      dateTo: filterDateTo?.toISOString() || null,
      maxDistance: filterMaxDistance,
      minCost: filterMinCost,
      maxCost: filterMaxCost,
      venueType: filterVenueType,
      keywords: filterKeywords,
    };
    
    const updatedFilters = [...savedFilters, newFilter];
    setSavedFilters(updatedFilters);
    localStorage.setItem('eventSavedFilters', JSON.stringify(updatedFilters));
    setSaveFilterDialogOpen(false);
    setFilterName('');
    toast.success(`Filter "${newFilter.name}" saved!`);
  };

  // Load a saved filter
  const loadFilter = (filter) => {
    setFilterEventType(filter.eventType || '');
    setFilterDateFrom(filter.dateFrom ? new Date(filter.dateFrom) : null);
    setFilterDateTo(filter.dateTo ? new Date(filter.dateTo) : null);
    setFilterMaxDistance(filter.maxDistance || '');
    setFilterMinCost(filter.minCost || '');
    setFilterMaxCost(filter.maxCost || '');
    setFilterVenueType(filter.venueType || '');
    setFilterKeywords(filter.keywords || '');
    setShowFilters(true);
    toast.success(`Loaded filter: ${filter.name}`);
  };

  // Delete a saved filter
  const deleteFilter = (filterId) => {
    const updatedFilters = savedFilters.filter(f => f.id !== filterId);
    setSavedFilters(updatedFilters);
    localStorage.setItem('eventSavedFilters', JSON.stringify(updatedFilters));
    toast.success('Filter deleted');
  };

  // Fetch home coordinates
  useEffect(() => {
    const fetchHomeCoords = async () => {
      if (user?.home_city && user?.home_state) {
        try {
          const res = await geocodeAPI.getCoords(user.home_city, user.home_state);
          setHomeCoords(res.data);
        } catch (error) {
          // Fallback to state-level coords
          const stateCoords = getStateCoords(user.home_state);
          if (stateCoords) setHomeCoords(stateCoords);
        }
      }
    };
    fetchHomeCoords();
  }, [user?.home_city, user?.home_state]);

  const fetchEvents = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      // Get all favorited events - these are "My Events" (Saved)
      const favRes = await favoritesAPI.getAll();
      setMyEvents(favRes.data);
      setFavoriteIds(new Set(favRes.data.map(e => e.id)));
      
      // Get user's own created events
      const userCreatedRes = await eventsAPI.getMyCreated();
      setUserCreatedEvents(userCreatedRes.data);
      
      // Get ALL events in the system (community events)
      const allRes = await eventsAPI.getAll();
      setAllEvents(allRes.data);
      
      // Community events = events marked as is_community: true (including user's own shared events)
      const communityEvts = allRes.data.filter(e => e.is_community === true);
      setCommunityEvents(communityEvts);
      
      // Fetch count of expired community events
      try {
        const expiredRes = await eventsAPI.getExpiredCommunityCount();
        setExpiredCommunityCount(expiredRes.data.expired_count || 0);
      } catch (err) {
        console.warn('Failed to fetch expired community count');
      }
      
      // Combine events for geocoding and application status
      const combinedEvents = [...favRes.data];
      // Add events from allRes that aren't already in favRes
      const favIds = new Set(favRes.data.map(e => e.id));
      allRes.data.forEach(e => {
        if (!favIds.has(e.id)) {
          combinedEvents.push(e);
        }
      });
      
      // Fetch application status for all events
      if (combinedEvents.length > 0) {
        const eventIds = combinedEvents.map(e => e.id);
        try {
          const appRes = await applicationsAPI.getByEvents(eventIds);
          setApplicationStatusMap(appRes.data);
        } catch (error) {
          console.warn('Failed to fetch application status');
        }
      }
      
      // Batch geocode event locations (limit to first 50 for performance)
      // This runs in background and doesn't block loading
      const locationsToGeocode = combinedEvents
        .slice(0, 50)  // Limit for performance
        .map(e => ({ 
          location: e.location, 
          city: e.city, 
          state: e.state,
          zip: e.zip 
        }));
      
      if (locationsToGeocode.length > 0) {
        // Don't await - let it run in background
        geocodeAPI.batch(locationsToGeocode)
          .then(coordsRes => {
            const cache = {};
            coordsRes.data.forEach(item => {
              if (item.result) {
                cache[item.address] = item.result;
              }
            });
            setCoordsCache(cache);
          })
          .catch(error => {
            console.warn('Batch geocoding failed, using fallback');
          });
      }
    } catch (error) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Refetch events when navigating back to this page
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Trigger refetch when returning to this page (e.g., after editing an event)
  useEffect(() => {
    if (location.key !== 'default') {
      setLastRefresh(Date.now());
      fetchEvents();
    }
  }, [location.key]);

  // Get distance for an event using geocoded coordinates
  const getDistance = useCallback((event) => {
    if (!homeCoords || !event.city || !event.state) return null;
    
    // Get home longitude (handle both 'lng' and 'lon' property names)
    const homeLng = homeCoords.lng || homeCoords.lon;
    if (!homeCoords.lat || !homeLng) return null;
    
    // Build the cache key with full address
    const parts = [];
    if (event.location) parts.push(event.location);
    if (event.city) parts.push(event.city);
    if (event.state) parts.push(event.state);
    if (event.zip) parts.push(event.zip);
    const cacheKey = parts.join(', ');
    
    // Try to get geocoded coords for this event
    const eventCoords = coordsCache[cacheKey];
    
    if (eventCoords) {
      return haversineDistance(homeCoords.lat, homeLng, eventCoords.lat, eventCoords.lon || eventCoords.lng);
    }
    
    // Try simpler key (city, state only)
    const simpleKey = `${event.city}, ${event.state}`;
    const simpleCoords = coordsCache[simpleKey];
    if (simpleCoords) {
      return haversineDistance(homeCoords.lat, homeLng, simpleCoords.lat, simpleCoords.lon || simpleCoords.lng);
    }
    
    // Fallback to state-level coords
    const stateCoords = getStateCoords(event.state);
    if (stateCoords) {
      return haversineDistance(homeCoords.lat, homeLng, stateCoords.lat, stateCoords.lng);
    }
    
    return null;
  }, [homeCoords, coordsCache]);

  // Generic filter function that works on any event list
  const applyFilters = useCallback((events) => {
    let filtered = [...events];
    
    // Filter expired events when hideExpired is enabled
    if (hideExpired) {
      filtered = filtered.filter(e => !isEventExpired(e));
    }
    
    // Filter by event type
    if (filterEventType) {
      filtered = filtered.filter(e => e.event_type === filterEventType);
    }
    
    // Filter by date range
    if (filterDateFrom) {
      const fromDate = filterDateFrom.toISOString().split('T')[0];
      filtered = filtered.filter(e => e.date >= fromDate);
    }
    if (filterDateTo) {
      const toDate = filterDateTo.toISOString().split('T')[0];
      filtered = filtered.filter(e => e.date <= toDate);
    }
    
    // Filter by distance (requires getDistance function)
    if (filterMaxDistance && homeCoords) {
      const maxDist = parseInt(filterMaxDistance);
      filtered = filtered.filter(e => {
        const dist = getDistance(e);
        return dist === null || dist <= maxDist;
      });
    }
    
    // Filter by booth cost
    if (filterMinCost) {
      const minCost = parseFloat(filterMinCost);
      filtered = filtered.filter(e => e.booth_cost === null || e.booth_cost >= minCost);
    }
    if (filterMaxCost) {
      const maxCost = parseFloat(filterMaxCost);
      filtered = filtered.filter(e => e.booth_cost === null || e.booth_cost <= maxCost);
    }
    
    // Filter by venue type (indoor/outdoor)
    if (filterVenueType) {
      filtered = filtered.filter(e => {
        if (filterVenueType === 'indoor') return e.indoor_outdoor === 'indoor' || e.venue_type?.toLowerCase().includes('indoor');
        if (filterVenueType === 'outdoor') return e.indoor_outdoor === 'outdoor' || e.venue_type?.toLowerCase().includes('outdoor');
        return true;
      });
    }
    
    // Filter by keywords
    if (filterKeywords) {
      const keywords = filterKeywords.toLowerCase().split(',').map(k => k.trim());
      filtered = filtered.filter(e => {
        const searchText = `${e.title} ${e.description || ''} ${e.event_type || ''} ${e.venue_type || ''}`.toLowerCase();
        return keywords.some(kw => searchText.includes(kw));
      });
    }
    
    return filtered;
  }, [hideExpired, filterEventType, filterDateFrom, filterDateTo, filterMaxDistance, filterMinCost, filterMaxCost, filterVenueType, filterKeywords, homeCoords, getDistance]);

  // Apply filters to community events - also filter by user's preferred radius
  const filteredCommunityEvents = useMemo(() => {
    let filtered = applyFilters(communityEvents);
    
    // If no explicit distance filter is set, apply user's preferred max_distance
    if (!filterMaxDistance && user?.max_distance && homeCoords) {
      const maxDist = parseInt(user.max_distance);
      filtered = filtered.filter(e => {
        const dist = getDistance(e);
        // Include events with no distance (can't calculate) or within range
        return dist === null || dist <= maxDist;
      });
    }
    
    return filtered;
  }, [communityEvents, applyFilters, filterMaxDistance, user?.max_distance, homeCoords, getDistance]);

  // Determine which events to display based on tab and user settings
  const getDisplayEvents = useCallback(() => {
    let baseEvents;
    
    if (eventTab === 'saved') {
      baseEvents = myEvents;
    } else if (eventTab === 'community') {
      // Use pre-filtered community events (applies user's preferred radius)
      baseEvents = filteredCommunityEvents;
      // Apply additional filters (including hideExpired)
      return applyFilters(baseEvents);
    } else {
      // 'all' tab - user's own events + favorites (when community events is OFF)
      const ownAndFavIds = new Set([...userCreatedEvents.map(e => e.id), ...myEvents.map(e => e.id)]);
      baseEvents = allEvents.filter(e => ownAndFavIds.has(e.id));
    }
    
    // Always apply filters (includes hideExpired and any active custom filters)
    return applyFilters(baseEvents);
  }, [eventTab, myEvents, filteredCommunityEvents, applyFilters, userCreatedEvents, allEvents]);
  
  // Get base events, then apply sorting
  // Sort directly in useMemo to ensure proper reactivity when sortBy changes
  const displayEvents = useMemo(() => {
    const baseEvents = getDisplayEvents();
    if (!baseEvents || baseEvents.length === 0) return baseEvents;
    
    const sorted = [...baseEvents];
    
    switch (sortBy) {
      case 'priority':
        // Sort by priority: Accepted > In Progress > Waitlisted > Rejected > Saved > My Events > Community
        return sortEventsByPriority(sorted, applicationStatusMap, favoriteIds);
      
      case 'date':
        // Sort by date (earliest first)
        return sorted.sort((a, b) => {
          const dateA = a.date || '';
          const dateB = b.date || '';
          return dateA.localeCompare(dateB);
        });
      
      case 'distance':
        // Sort by distance (closest first), events without distance go to end
        return sorted.sort((a, b) => {
          const distA = getDistance(a);
          const distB = getDistance(b);
          // Put events without distance at the end
          if (distA === null && distB === null) return 0;
          if (distA === null) return 1;
          if (distB === null) return -1;
          return distA - distB;
        });
      
      case 'title':
        // Sort alphabetically by title (A-Z)
        return sorted.sort((a, b) => {
          const titleA = (a.title || '').toLowerCase();
          const titleB = (b.title || '').toLowerCase();
          return titleA.localeCompare(titleB);
        });
      
      case 'deadline':
        // Sort by application deadline (earliest first), events without deadline go to end
        return sorted.sort((a, b) => {
          const deadlineA = a.deadline || '';
          const deadlineB = b.deadline || '';
          // Put events without deadline at the end
          if (!deadlineA && !deadlineB) return 0;
          if (!deadlineA) return 1;
          if (!deadlineB) return -1;
          return deadlineA.localeCompare(deadlineB);
        });
      
      default:
        return sorted;
    }
  }, [getDisplayEvents, sortBy, getDistance, applicationStatusMap, favoriteIds]);
  
  // Helper to get all dates between start and end (inclusive)
  const getDateRange = (startStr, endStr) => {
    const dates = [];
    if (!startStr) return dates;
    
    const [startYear, startMonth, startDay] = startStr.split('-').map(Number);
    const start = new Date(startYear, startMonth - 1, startDay);
    
    if (!endStr) {
      dates.push(start);
      return dates;
    }
    
    const [endYear, endMonth, endDay] = endStr.split('-').map(Number);
    const end = new Date(endYear, endMonth - 1, endDay);
    
    const current = new Date(start);
    while (current <= end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };
  
  // Check if an event spans a specific date (includes all dates in range and additional dates)
  // For farmers markets with recurring schedules, only match computed dates
  const eventSpansDate = (event, dateStr) => {
    // If event has recurring_schedule with computed_dates, use ONLY those
    if (event.recurring_schedule?.computed_dates?.length > 0) {
      return event.recurring_schedule.computed_dates.includes(dateStr);
    }
    
    // Check primary date range
    if (event.date === dateStr) return true;
    
    // Check if dateStr falls within primary date range (date to end_date)
    if (event.date && event.end_date) {
      const [year, month, day] = dateStr.split('-').map(Number);
      const checkDate = new Date(year, month - 1, day);
      const [startYear, startMonth, startDay] = event.date.split('-').map(Number);
      const startDate = new Date(startYear, startMonth - 1, startDay);
      const [endYear, endMonth, endDay] = event.end_date.split('-').map(Number);
      const endDate = new Date(endYear, endMonth - 1, endDay);
      if (checkDate >= startDate && checkDate <= endDate) return true;
    }
    
    // Check additional date ranges
    if (event.event_dates && Array.isArray(event.event_dates)) {
      for (const range of event.event_dates) {
        if (range.start_date === dateStr) return true;
        if (range.start_date && range.end_date) {
          const [year, month, day] = dateStr.split('-').map(Number);
          const checkDate = new Date(year, month - 1, day);
          const [startYear, startMonth, startDay] = range.start_date.split('-').map(Number);
          const startDate = new Date(startYear, startMonth - 1, startDay);
          const [endYear, endMonth, endDay] = range.end_date.split('-').map(Number);
          const endDate = new Date(endYear, endMonth - 1, endDay);
          if (checkDate >= startDate && checkDate <= endDate) return true;
        }
      }
    }
    
    return false;
  };
  
  const getEventsForDate = (date) => {
    // Use local date string to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    // Check if any event spans this date (includes multi-day events)
    return displayEvents.filter(e => eventSpansDate(e, dateStr));
  };

  // Get ALL dates that have events (for calendar highlighting)
  // For recurring events (farmers markets), use computed_dates instead of full range
  const eventDates = useMemo(() => {
    const allDates = [];
    displayEvents.forEach(e => {
      // If event has recurring_schedule with computed_dates, use those
      if (e.recurring_schedule?.computed_dates?.length > 0) {
        e.recurring_schedule.computed_dates.forEach(d => allDates.push(d));
        return;
      }
      
      // Add all dates in primary range
      getDateRange(e.date, e.end_date).forEach(d => allDates.push(d));
      
      // Add all dates from additional date ranges
      if (e.event_dates && Array.isArray(e.event_dates)) {
        e.event_dates.forEach(range => {
          getDateRange(range.start_date, range.end_date).forEach(d => allDates.push(d));
        });
      }
    });
    return allDates;
  }, [displayEvents]);

  // Handler for removing expired community events
  const handleDeleteExpiredCommunity = async () => {
    if (expiredCommunityCount === 0) return;
    
    setDeletingExpired(true);
    try {
      const res = await eventsAPI.deleteExpiredCommunity();
      const deletedCount = res.data.deleted_count || 0;
      
      toast.success(`Removed ${deletedCount} expired event${deletedCount !== 1 ? 's' : ''}`);
      
      // Reset count and refresh events
      setExpiredCommunityCount(0);
      setLastRefresh(Date.now());  // Trigger refresh
    } catch (error) {
      console.error('Failed to delete expired events:', error);
      toast.error('Failed to remove expired events');
    } finally {
      setDeletingExpired(false);
    }
  };

  const viewButtons = [
    { id: 'calendar', label: 'Calendar', icon: CalendarDays },
    { id: 'year', label: 'Year', icon: CalendarRange },
    { id: 'card', label: 'Cards', icon: LayoutGrid },
    { id: 'table', label: 'Table', icon: TableIcon },
    { id: 'map', label: 'Map', icon: MapPin },
  ];

  const saveFilterDialogEl = (
    <Dialog open={saveFilterDialogOpen} onOpenChange={setSaveFilterDialogOpen}>
      <DialogContent className="border-2 border-[#1E3A5F]">
        <DialogHeader>
          <DialogTitle className="text-[#1E3A5F]">Save Filter</DialogTitle>
          <DialogDescription>
            Save your current filter configuration for quick access later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Filter Name</Label>
            <Input
              placeholder="e.g., Local Holiday Markets"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              className="input-default"
              data-testid="filter-name-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setSaveFilterDialogOpen(false)}>
            Cancel
          </Button>
          <Button className="btn-primary" onClick={saveCurrentFilter}>
            Save Filter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (!user) {
    return (
      <div className="min-h-screen py-8 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="card-dashboard text-center py-16">
            <Heart className="w-16 h-16 text-[#71717A] mx-auto mb-4" />
            <h3 className="text-xl font-bold font-['Outfit'] text-[#1E3A5F] mb-2">
              Login Required
            </h3>
            <p className="text-[#4A4A4A] mb-6">
              Please log in to view your saved events
            </p>
            <Link to="/login">
              <Button className="btn-primary">Log in</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Full-screen immersive Map view (fills area below the sticky header)
  if (viewMode === 'map') {
    return (
      <div className="h-[calc(100vh-4rem)] md:h-[calc(100vh-5rem)] w-full overflow-hidden">
        {saveFilterDialogEl}
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-[#1E3A5F] animate-spin" />
          </div>
        ) : (
          <MapView
            user={user}
            events={displayEvents}
            getDistance={getDistance}
            favoriteIds={favoriteIds}
            applicationStatusMap={applicationStatusMap}
            homeCoords={homeCoords}
            homeRadiusMiles={user?.max_distance ? parseInt(user.max_distance) : 100}
            onEventsChanged={fetchEvents}
            panelProps={{
              eventTab,
              setEventTab,
              savedCount: myEvents.length,
              allCount: new Set([
                ...userCreatedEvents.map((e) => e.id),
                ...myEvents.map((e) => e.id),
              ]).size,
              communityCount: filteredCommunityEvents.length,
              viewCommunityEnabled: !!user?.view_community_events,
              allTabLabel: user?.view_community_events ? 'My Events' : 'All My Events',
              setViewMode,
              filterEventType,
              setFilterEventType,
              filterVenueType,
              setFilterVenueType,
              filterDateFrom,
              setFilterDateFrom,
              filterDateTo,
              setFilterDateTo,
              filterMaxDistance,
              setFilterMaxDistance,
              filterMinCost,
              setFilterMinCost,
              filterMaxCost,
              setFilterMaxCost,
              filterKeywords,
              setFilterKeywords,
              hasActiveFilters,
              clearFilters,
              sortBy,
              setSortBy,
              hideExpired,
              setHideExpired,
              savedFilters,
              loadFilter,
              deleteFilter,
              openSaveFilterDialog: () => setSaveFilterDialogOpen(true),
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div className="flex items-center gap-2">
            <div>
              <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight text-[#1E3A5F] mb-2">
                My Events
              </h1>
              <p className="text-lg text-[#4A4A4A]">
                {displayEvents.length} event{displayEvents.length !== 1 ? 's' : ''}
                {eventTab === 'saved' && ' saved'}
              </p>
            </div>
            <HelpButton tutorialId="events" />
          </div>

          <div className="flex items-center gap-4">
            {/* Add Event Button */}
            <Link to="/add-event">
              <Button 
                className="bg-[#1E3A5F] hover:bg-[#1E3A5F] text-white font-bold"
                data-testid="add-event-btn"
              >
                <CalendarDays className="w-4 h-4 mr-2" />
                Add Event
              </Button>
            </Link>

            {/* View Toggle */}
            <div className="flex items-center gap-2 p-1 bg-[#FFEDDF] rounded-xl border-2 border-[#1E3A5F]">
              {viewButtons.map((btn) => (
                <button
                  key={btn.id}
                  onClick={() => setViewMode(btn.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    viewMode === btn.id
                      ? 'bg-[#1E3A5F] text-white'
                      : 'text-[#1E3A5F] hover:bg-[#1E3A5F]/20'
                  }`}
                  data-testid={`view-${btn.id}`}
                >
                  <btn.icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{btn.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Saved/All/Community Events Tabs */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <button
            onClick={() => setEventTab('saved')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-colors ${
              eventTab === 'saved'
                ? 'bg-[#1E3A5F] text-white shadow-hard'
                : 'bg-white text-[#1E3A5F] border-2 border-[#1E3A5F] hover:bg-[#FFEDDF]'
            }`}
            data-testid="tab-saved-events"
          >
            <Heart className="w-4 h-4" />
            Saved Events ({myEvents.length})
          </button>
          
          <button
            onClick={() => setEventTab('all')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-colors ${
              eventTab === 'all'
                ? 'bg-[#1E3A5F] text-white shadow-hard'
                : 'bg-white text-[#1E3A5F] border-2 border-[#1E3A5F] hover:bg-[#FFEDDF]'
            }`}
            data-testid="tab-all-events"
          >
            <CalendarDays className="w-4 h-4" />
            {user?.view_community_events ? 'My Events' : 'All My Events'} ({new Set([...userCreatedEvents.map(e => e.id), ...myEvents.map(e => e.id)]).size})
          </button>
          
          {/* Community Events Tab - Only show when setting is ENABLED */}
          {user?.view_community_events && (
            <button
              onClick={() => { setEventTab('community'); setShowFilters(true); }}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-colors ${
                eventTab === 'community'
                  ? 'bg-[#1E3A5F] text-white shadow-hard'
                  : 'bg-[#FFEDDF] text-[#1E3A5F] border-2 border-[#1E3A5F] hover:bg-[#1E3A5F]/20'
              }`}
              data-testid="tab-community-events"
            >
              <Users className="w-4 h-4" />
              Community Events ({filteredCommunityEvents.length})
            </button>
          )}
          
          {/* Enable Community Events Hint - Only show when setting is DISABLED */}
          {!user?.view_community_events && (
            <Link to="/settings" className="text-sm text-[#1E3A5F] hover:underline flex items-center gap-1">
              <Users className="w-4 h-4" />
              Enable Community Events in Settings
            </Link>
          )}
        </div>

        {/* Community Events Helper Text */}
        {eventTab === 'community' && (
          <div className="mb-4 p-4 bg-[#1E3A5F]/10 border border-[#1E3A5F]/30 rounded-xl">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <p className="text-[#1E3A5F] text-sm flex-1">
                <span className="font-semibold">
                  {filteredCommunityEvents.length === 0 
                    ? 'No community events match your filters.' 
                    : `Showing ${filteredCommunityEvents.length} of ${communityEvents.length} Community ${communityEvents.length === 1 ? 'event' : 'events'} within your preferred distance.`}
                </span>
                {' '}Adjust filters or distance settings to see more events.
              </p>
              
              {/* Remove Expired Events Button */}
              {expiredCommunityCount > 0 && (
                <button
                  onClick={handleDeleteExpiredCommunity}
                  disabled={deletingExpired}
                  className="flex items-center gap-2 px-3 py-1.5 bg-[#FE5F55]/10 text-[#FE5F55] hover:bg-[#FE5F55]/20 border border-[#FE5F55]/30 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                  data-testid="remove-expired-btn"
                >
                  {deletingExpired ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Remove {expiredCommunityCount} Expired
                </button>
              )}
            </div>
          </div>
        )}

        {/* Event Filters (available on all tabs) */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors ${
                  showFilters ? 'bg-[#1E3A5F] text-white' : 'bg-[#FFEDDF] text-[#1E3A5F] hover:bg-[#1E3A5F]/20'
                }`}
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-4 h-4" />
                {showFilters ? 'Hide Filters' : 'Filters'}
                {hasActiveFilters && <span className="px-2 py-0.5 bg-[#FE5F55] text-white text-xs rounded-full ml-2">Active</span>}
              </button>
              
              {/* Saved Filters Dropdown */}
              {savedFilters.length > 0 && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="border-2 border-[#1E3A5F] text-[#1E3A5F]" data-testid="saved-filters-btn">
                      <Bookmark className="w-4 h-4 mr-2" />
                      Saved Filters ({savedFilters.length})
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2 border-2 border-[#1E3A5F] rounded-xl" align="start">
                    <div className="space-y-1">
                      {savedFilters.map((filter) => (
                        <div key={filter.id} className="flex items-center justify-between p-2 hover:bg-[#FFEDDF] rounded-lg">
                          <button
                            onClick={() => loadFilter(filter)}
                            className="flex-1 text-left text-sm text-[#1E3A5F] font-medium"
                          >
                            {filter.name}
                          </button>
                          <button
                            onClick={() => deleteFilter(filter.id)}
                            className="p-1 text-gray-400 hover:text-[#FE5F55]"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              
              {/* Sort Dropdown */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px] border-2 border-[#1E3A5F] bg-white text-[#1E3A5F] rounded-xl" data-testid="sort-dropdown">
                  <ArrowUpDown className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent className="border-2 border-[#1E3A5F] rounded-xl">
                  <SelectItem value="priority" data-testid="sort-by-priority">
                    <span className="flex items-center gap-2">
                      <Star className="w-4 h-4 text-purple-600" />
                      Priority (Apps First)
                    </span>
                  </SelectItem>
                  <SelectItem value="date" data-testid="sort-by-date">
                    <span className="flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4" />
                      Date (Earliest)
                    </span>
                  </SelectItem>
                  <SelectItem value="distance" data-testid="sort-by-distance">
                    <span className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Distance (Closest)
                    </span>
                  </SelectItem>
                  <SelectItem value="title" data-testid="sort-by-title">
                    <span className="flex items-center gap-2">
                      <span className="font-bold text-xs">A-Z</span>
                      Alphabetical
                    </span>
                  </SelectItem>
                  <SelectItem value="deadline" data-testid="sort-by-deadline">
                    <span className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Deadline (Earliest)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>

              {/* Hide Expired Toggle */}
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-xl border-2 border-[#1E3A5F]">
                <Switch
                  id="hide-expired"
                  checked={hideExpired}
                  onCheckedChange={setHideExpired}
                  data-testid="hide-expired-toggle"
                />
                <Label 
                  htmlFor="hide-expired" 
                  className="text-sm font-medium text-[#1E3A5F] cursor-pointer flex items-center gap-1.5"
                >
                  <EyeOff className="w-4 h-4" />
                  Hide Expired
                </Label>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {hasActiveFilters && (
                <>
                  <button
                    onClick={() => setSaveFilterDialogOpen(true)}
                    className="flex items-center gap-2 text-sm text-[#1E3A5F] hover:text-[#1E3A5F]"
                    data-testid="save-filter-btn"
                  >
                    <BookmarkPlus className="w-4 h-4" />
                    Save Filter
                  </button>
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-2 text-sm text-[#FE5F55] hover:text-[#1E3A5F]"
                    data-testid="clear-filters-btn"
                  >
                    <X className="w-4 h-4" />
                    Clear
                  </button>
                </>
              )}
            </div>
          </div>

          {showFilters && (
            <div className="p-6 bg-white rounded-xl border-2 border-[#1E3A5F] shadow-hard space-y-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Event Type */}
                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">Event Type</Label>
                  <Select value={filterEventType || "all_types"} onValueChange={(v) => setFilterEventType(v === "all_types" ? "" : v)}>
                    <SelectTrigger className="input-default" data-testid="filter-event-type">
                      <SelectValue placeholder="All types" />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-[#1E3A5F] rounded-xl">
                      <SelectItem value="all_types">All types</SelectItem>
                      {Object.entries(EVENT_TYPES).map(([key, { label }]) => (
                        <SelectItem key={key} value={key}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Venue Type */}
                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">Venue Type</Label>
                  <Select value={filterVenueType || "any_venue"} onValueChange={(v) => setFilterVenueType(v === "any_venue" ? "" : v)}>
                    <SelectTrigger className="input-default" data-testid="filter-venue-type">
                      <SelectValue placeholder="Indoor/Outdoor" />
                    </SelectTrigger>
                    <SelectContent className="border-2 border-[#1E3A5F] rounded-xl">
                      <SelectItem value="any_venue">Any</SelectItem>
                      <SelectItem value="indoor">Indoor</SelectItem>
                      <SelectItem value="outdoor">Outdoor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Date From */}
                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">From Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "input-default w-full justify-start text-left font-normal",
                          !filterDateFrom && "text-gray-400"
                        )}
                        data-testid="filter-date-from"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filterDateFrom ? format(filterDateFrom, 'MMM d, yyyy') : 'Start date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 border-2 border-[#1E3A5F] rounded-xl" align="start">
                      <Calendar
                        mode="single"
                        selected={filterDateFrom}
                        onSelect={setFilterDateFrom}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Date To */}
                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">To Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "input-default w-full justify-start text-left font-normal",
                          !filterDateTo && "text-gray-400"
                        )}
                        data-testid="filter-date-to"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filterDateTo ? format(filterDateTo, 'MMM d, yyyy') : 'End date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 border-2 border-[#1E3A5F] rounded-xl" align="start">
                      <Calendar
                        mode="single"
                        selected={filterDateTo}
                        onSelect={setFilterDateTo}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Max Distance */}
                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">Max Distance (mi)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 100"
                    value={filterMaxDistance}
                    onChange={(e) => setFilterMaxDistance(e.target.value)}
                    className="input-default"
                    data-testid="filter-max-distance"
                  />
                </div>

                {/* Min Booth Cost */}
                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">Min Booth Cost ($)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 50"
                    value={filterMinCost}
                    onChange={(e) => setFilterMinCost(e.target.value)}
                    className="input-default"
                    data-testid="filter-min-cost"
                  />
                </div>

                {/* Max Booth Cost */}
                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">Max Booth Cost ($)</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 500"
                    value={filterMaxCost}
                    onChange={(e) => setFilterMaxCost(e.target.value)}
                    className="input-default"
                    data-testid="filter-max-cost"
                  />
                </div>

                {/* Keywords */}
                <div className="space-y-2">
                  <Label className="font-medium text-[#1E3A5F]">Keywords</Label>
                  <Input
                    placeholder="e.g., jewelry, faeries, holiday"
                    value={filterKeywords}
                    onChange={(e) => setFilterKeywords(e.target.value)}
                    className="input-default"
                    data-testid="filter-keywords"
                  />
                  <p className="text-xs text-gray-500">Comma-separated</p>
                </div>
              </div>

              {hasActiveFilters && (
                <div className="flex items-center gap-2 text-sm text-[#1E3A5F]">
                  <Search className="w-4 h-4" />
                  Showing {displayEvents.length} events
                  {eventTab === 'community' && ` of ${communityEvents.length} community events`}
                  {eventTab === 'saved' && ` of ${myEvents.length} saved events`}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Save Filter Dialog */}
        {saveFilterDialogEl}

        {/* Home Location Warning */}
        {!user.home_state && (
          <div className="mb-6 p-4 bg-[#1E3A5F]/10 border-2 border-[#1E3A5F] rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-[#1E3A5F] flex-shrink-0" />
            <p className="text-sm text-[#1E3A5F]">
              Set your home location in{' '}
              <Link to="/settings" className="font-semibold underline">Settings</Link>
              {' '}to see distances to events.
            </p>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#1E3A5F] animate-spin" />
          </div>
        ) : (
          <>
            {/* Calendar View - Always show calendar, even with no events */}
            {viewMode === 'calendar' && (
              <CalendarView 
                key={`calendar-${eventTab}`}
                favorites={displayEvents}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                eventDates={eventDates}
                getEventsForDate={getEventsForDate}
                getDistance={getDistance}
                fetchFavorites={fetchEvents}
                favoriteIds={favoriteIds}
                eventTab={eventTab}
                applicationStatusMap={applicationStatusMap}
              />
            )}

            {/* Year View - Multi-month calendar */}
            {viewMode === 'year' && (
              <YearView 
                events={displayEvents}
                userCreatedEvents={userCreatedEvents}
                favoriteIds={favoriteIds}
                applicationStatusMap={applicationStatusMap}
              />
            )}

            {/* Other views - show empty state if no events */}
            {viewMode !== 'calendar' && viewMode !== 'year' && displayEvents.length === 0 ? (
              <div className="card-dashboard text-center py-16">
                <Heart className="w-16 h-16 text-[#71717A] mx-auto mb-4" />
                <h3 className="text-xl font-bold font-['Outfit'] text-[#1E3A5F] mb-2">
                  No events found
                </h3>
                <p className="text-[#4A4A4A] mb-6">
                  {eventTab === 'saved' 
                    ? 'Use Event Scout to discover events and save them here'
                    : 'No events available yet. Add some events to get started!'}
                </p>
                <Link to="/ai-discover">
                  <Button className="btn-primary">
                    Find Events
                  </Button>
                </Link>
              </div>
            ) : (
              <>
            {viewMode === 'card' && (
              <CardsView
                events={displayEvents}
                getDistance={getDistance}
                fetchEvents={fetchEvents}
                favoriteIds={favoriteIds}
                applicationStatusMap={applicationStatusMap}
                eventTab={eventTab}
                currentUserId={user?.id}
              />
            )}

            {/* Table View */}
            {viewMode === 'table' && (
              <TableView
                events={displayEvents}
                getDistance={getDistance}
                favoriteIds={favoriteIds}
                applicationStatusMap={applicationStatusMap}
                homeState={user.home_state}
              />
            )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};



export default Events;
